import Box from "@mui/material/Box";
import React from "react";

const commonSvg = (size: number, animate: boolean, children: React.ReactNode) => (
  <Box
    component="svg"
    viewBox="0 0 120 120"
    width={size}
    height={size}
    className="party-blob"
    sx={animate ? { animation: "party-floaty 4s ease-in-out infinite" } : undefined}
  >
    {children}
  </Box>
);

export function PartyMascot({
  shellId,
  size = 120,
  animate = true,
  colors,
}: {
  shellId: string;
  size?: number;
  animate?: boolean;
  colors?: {
    hot: string;
    gold: string;
    violet: string;
    mint: string;
  };
}) {
  const hot = colors?.hot ?? "var(--hot)";
  const gold = colors?.gold ?? "var(--gold)";
  const violet = colors?.violet ?? "var(--violet)";
  const mint = colors?.mint ?? "var(--mint)";

  switch (shellId) {
    case "guess":
      return commonSvg(size, animate, (
        <>
          <path
            d="M18 30 Q18 14 34 14 L86 14 Q102 14 102 30 L102 66 Q102 82 86 82 L52 82 L36 100 L40 82 L34 82 Q18 82 18 66 Z"
            style={{ fill: hot }}
          />
          <path
            d="M46 36 L74 64 M74 36 L46 64"
            stroke="#fff"
            strokeWidth="8"
            strokeLinecap="round"
          />
        </>
      ));
    case "acting":
    case "act":
      return commonSvg(size, animate, (
        <>
          <circle cx="72" cy="26" r="14" style={{ fill: gold }} />
          <path
            d="M44 94 L62 60 L88 66 L100 46"
            style={{ stroke: gold }}
            strokeWidth="14"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M62 60 L50 82 L30 86"
            style={{ stroke: gold }}
            strokeWidth="12"
            strokeLinecap="round"
            fill="none"
          />
        </>
      ));
    case "imposter-online":
    case "imposter":
      return commonSvg(size, animate, (
        <>
          <path d="M60 12 L108 60 L60 108 L12 60 Z" style={{ fill: violet }} />
          <ellipse cx="60" cy="60" rx="26" ry="10" fill="#0E0B1A" />
          <circle cx="60" cy="60" r="5" style={{ fill: hot }} />
        </>
      ));
    case "whoami":
    case "who":
      return commonSvg(size, animate, (
        <>
          <circle cx="60" cy="60" r="50" style={{ fill: mint }} />
          <path
            d="M44 46 Q44 30 60 30 Q76 30 76 46 Q76 58 60 62 L60 74"
            stroke="#0E0B1A"
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="60" cy="92" r="6" fill="#0E0B1A" />
        </>
      ));
    case "answer_bluff":
    case "bluff":
      return commonSvg(size, animate, (
        <>
          <circle cx="44" cy="60" r="30" style={{ fill: hot }} />
          <path d="M64 52 L108 50 L102 60 L108 70 L64 68 Z" style={{ fill: gold }} />
          <circle cx="34" cy="52" r="4" fill="#0E0B1A" />
          <path
            d="M34 72 Q44 80 54 72"
            stroke="#0E0B1A"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
        </>
      ));
    case "quick_questions":
    case "fast":
      return commonSvg(size, animate, (
        <path
          d="M66 8 L28 66 L52 66 L42 112 L92 50 L66 50 L78 8 Z"
          style={{ fill: gold }}
          stroke="#0E0B1A"
          strokeWidth="3"
          strokeLinejoin="round"
        />
      ));
    case "category":
      return commonSvg(size, animate, (
        <>
          <ellipse cx="60" cy="96" rx="38" ry="10" style={{ fill: mint, opacity: 0.3 }} />
          <rect x="26" y="70" width="68" height="18" rx="9" style={{ fill: mint }} />
          <rect x="30" y="50" width="60" height="18" rx="9" style={{ fill: hot }} />
          <rect x="34" y="30" width="52" height="18" rx="9" style={{ fill: gold }} />
        </>
      ));
    case "dare":
    case "challenge":
      return commonSvg(size, animate, (
        <>
          <path
            d="M60 10 L70 46 L108 46 L78 68 L90 104 L60 82 L30 104 L42 68 L12 46 L50 46 Z"
            style={{ fill: violet }}
          />
          <circle cx="60" cy="62" r="12" style={{ fill: gold }} />
        </>
      ));
    default:
      return commonSvg(size, animate, <circle cx="60" cy="60" r="40" style={{ fill: hot }} />);
  }
}

const CONFETTI_IDS = [
  "guess",
  "act",
  "imposter",
  "who",
  "bluff",
  "fast",
  "category",
  "challenge",
];

export function PartyConfettiCards({ count = 6 }: { count?: number }) {
  const items = Array.from({ length: count }).map((_, i) => ({
    id: CONFETTI_IDS[i % CONFETTI_IDS.length],
    left: (i * 37 + 10) % 100,
    top: (i * 53 + 20) % 100,
    rot: ((i * 29) % 40) - 20,
    delay: i * 0.4,
  }));
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {items.map((it, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${it.left}%`,
            top: `${it.top}%`,
            transform: `rotate(${it.rot}deg)`,
            opacity: 0.35,
            animation: `party-floaty ${6 + (i % 4)}s ease-in-out ${it.delay}s infinite`,
          }}
        >
          <PartyMascot shellId={it.id} size={60} animate={false} />
        </Box>
      ))}
    </Box>
  );
}
